import { useCallback, useEffect, useState } from 'react';
import { fetchErrorAlerts, type PlatformErrorAlert } from '@/lib/platformLogs';

// ─────────────────────────────────────────────────────────────
// Alertes de pic d'erreurs : compteur live pour la navigation
// SuperAdmin (badge sur « Logs Système ») + liste pour le panneau.
// ─────────────────────────────────────────────────────────────

export default function usePlatformErrorAlerts(pollMs = 60000, enabled = true) {
  const [alerts, setAlerts] = useState<PlatformErrorAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    // 🚫 Aucune requête tant que l'utilisateur n'est pas authentifié :
    // sinon le back-office générait des 401 sur les pages publiques.
    if (!enabled) {
      setLoading(false);
      return;
    }
    try {
      const rows = await fetchErrorAlerts(true, 20);
      setAlerts(rows);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de charger les alertes');
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    void refresh();
    const onAlert = () => void refresh();
    window.addEventListener('zifek:platform-alert', onAlert);
    const timer = window.setInterval(() => void refresh(), pollMs);
    return () => {
      window.removeEventListener('zifek:platform-alert', onAlert);
      window.clearInterval(timer);
    };
  }, [refresh, pollMs]);

  const dismiss = useCallback((id: number) => {
    setAlerts((prev) => prev.filter((a) => a.id !== id));
  }, []);

  return { alerts, count: alerts.length, loading, error, refresh, dismiss };
}